/* eslint-disable @typescript-eslint/no-explicit-any */
import styled from 'styled-components';

import { DocumentLoader } from '@common/hooks/useDocument';
import { Table } from '@common/Table';
import { GridRowSelectionModel, GridValueFormatterParams, GridValueGetterParams } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { State } from '@store/index';
import { useNavigate, useParams } from 'react-router-dom';
import { DescriptionOutlined } from '@mui/icons-material';
import { Alert, AlertTitle, Stack } from '@mui/material';
import { RequestError } from '@requests/common';

const Container = styled.div`
  display: flex;
`;

export const INTERCOM_BASE = 'https://support.telnyx.com/en/articles/';

export const formatBytesToKiB = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = 2; // Number of decimal places
  const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const formatDate = (isoDateString: string) => {
  return new Date(isoDateString).toLocaleString();
};

export const stringByteSize = (str: string) => {
  let size = 0;
  for (let i = 0; i < str.length; i++) {
    const charCode = str.charCodeAt(i);
    if (charCode < 0x0080) {
      size += 1; // 1 byte for characters in the range 0-127
    } else if (charCode < 0x0800) {
      size += 2; // 2 bytes for characters in the range 128-2047
    } else {
      size += 3; // 3 bytes for characters in the range 2048-65535
    }
  }
  return size;
};

interface Props {
  files: any;
  filesError: RequestError | null;
  isLoading: boolean;
  documentLoader: DocumentLoader;
  embeddingFiles: any[];
  selectedFiles: GridRowSelectionModel;
  setSelectedFiles: React.Dispatch<GridRowSelectionModel>;
}

export const BucketFiles: React.FC<Props> = ({
  files,
  filesError,
  isLoading,
  embeddingFiles,
  selectedFiles,
  setSelectedFiles,
}) => {
  const navigate = useNavigate();
  const editBucketFiles = useSelector((state: State) => state.flow.editBucketFiles);

  const { service_id, bucketName } = useParams();

  const columns = [
    {
      field: 'name',
      headerName: 'File',
      flex: 300,
      sortable: true,
      renderCell: (params: GridValueFormatterParams<string>) => {
        return (
          <Stack direction='row' alignItems='center'>
            <DescriptionOutlined sx={{ fontSize: '2ch', marginRight: '12px' }} />
            {params.value}
          </Stack>
        );
      },
    },
    {
      field: 'embedded',
      headerName: 'Embedded',
      flex: 90,
      type: 'boolean',
      valueGetter: (params: GridValueGetterParams) => {
        if (embeddingFiles.some((file) => file.filename === params.id && file.status === 'success')) return true;
        return params.value;
      },
    },
    {
      field: 'size',
      headerName: 'Size',
      flex: 60,
      sortable: true,
      valueFormatter: (params: GridValueFormatterParams<number>) => formatBytesToKiB(params.value),
    },
    {
      field: 'last_modification',
      headerName: 'Updated At',
      flex: 120,
      type: 'date',
      sortable: true,
      valueFormatter: (params: GridValueFormatterParams<Date>) => {
        if (!params.value) return '';
        const valueAsDate = new Date(params.value);
        return valueAsDate.toLocaleString(undefined, {
          year: 'numeric', // numeric, 2-digit
          month: 'short', // numeric, 2-digit, long, short, narrow
          day: '2-digit', // numeric, 2-digit
          hour: '2-digit', // numeric, 2-digit
          minute: '2-digit', // numeric, 2-digit
          second: '2-digit', // numeric, 2-digit
        });
      },
    },
  ];

  if (filesError) {
    return (
      <Stack width='100%' height='100%' p={2}>
        <Alert severity='error'>
          <AlertTitle>Bucket Files Error</AlertTitle>
          {filesError.response?.data?.error}
        </Alert>
      </Stack>
    );
  }

  return (
    <Container>
      <Table
        // page width - navbar width - left sideNav width - left sideBar width - table padding
        width='calc(100vw - 60px - 240px - 400px - 50px)'
        height='calc(100vh - 61px)'
        data={files || []}
        columns={columns}
        selectionModel={selectedFiles}
        checkboxes={editBucketFiles === bucketName}
        density={'standard'}
        onSelectionModelChange={(newRows) => setSelectedFiles(newRows)}
        getRowId={(row) => row.name}
        loading={isLoading}
        onRowClick={({ row }) => {
          if (!editBucketFiles) navigate(`/${service_id}/buckets/${bucketName}/${row.name}`);
        }}
      />
    </Container>
  );
};
